import { useContext } from 'react'

import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArchive, faCog, faCopy, faPencil, faRotateLeft,
} from '@fortawesome/pro-light-svg-icons'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import { TemplatePageModel } from '@models/templatePage'

import {
  Box, IconWithMenu, Image, Link, ListItem, Text,
} from '@domain-group/suit-theme'

import useLoginToRealhub from '@hooks/useLoginToRealhub'
import useTemplatePagePartial from '@hooks/useTemplatePagePartial'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { PageContextPayload } from '../../index'

const confirmToggleVisibility = (params) => {
  const { toggleTemplatePagePartialVisibility, toggleVisibilityFn, templatePagePartial } = params
  const { hidden } = templatePagePartial

  const toggleTemplatePagePartialVisibilityPayload = {
    callbacks: {
      action: toggleVisibilityFn,
    },
    toastText: hidden ? 'Template Partial Unarchived' : 'Template Partial Archived',
  }

  swal
    .fire({
      title: hidden ? 'Unarchive Partial?' : 'Archive Partial',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: hidden ? 'Yes, unarchive it' : 'Yes, archive it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        toggleTemplatePagePartialVisibility(toggleTemplatePagePartialVisibilityPayload)
      }
    })
}

type DuplicateTemplatePagePartial = {
  duplicateFn: () => void,
  duplicateTemplatePagePartial: (duplicateTemplatePagePartialPayload: HandleCallbackActionParams) => void,
}

const confirmDuplicateTemplatePagePartial = (params: DuplicateTemplatePagePartial) => {
  const { duplicateFn, duplicateTemplatePagePartial } = params

  const duplicateTemplatePagePartialPayload: HandleCallbackActionParams = {
    callbacks: {
      action: duplicateFn,
    },
    toastText: 'Template Partial Duplicated',
  }
  swal
    .fire({
      confirmButtonColor: '#43ac6a',
      confirmButtonText: 'Yes, duplicate it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Duplicate Template Page Partial?',
    })
    .then(({ value }) => {
      if (value){
        duplicateTemplatePagePartial(duplicateTemplatePagePartialPayload)
      }
    })
}

interface TemplatePagePartialProps {
  templatePagePartial: TemplatePageModel,
}
const TemplatePagePartial = (props: TemplatePagePartialProps) => {
  const { templatePagePartial: initTemplatePagePartial } = props

  const templatePartialPayload = useTemplatePagePartial(initTemplatePagePartial)

  const {
    brand,
    callbacks: {
      toggleTemplatePagePartialVisibility: toggleVisibilityFn,
      duplicateTemplatePagePartial: duplicateFn,
    },
    templatePagePartial,
    templatePagePartial: {
      id, title, short_name, width, hidden, height, preview_image,
    },
  } = templatePartialPayload

  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      toggleTemplatePagePartialVisibility,
      duplicateTemplatePagePartial,
    },
  } = pageContext

  const loginToRealhubPayload = useLoginToRealhub({
    redirect: { key: 'editTemplatePage', params: { templatePageId: id } },
  })

  const {
    callbacks: { loginToRealhub },
    isBrandUser,
  } = loginToRealhubPayload

  const shouldRenderEditPartial = isBrandUser ? templatePagePartial?.designer_type === 'Brand' : true

  return (
    <ListItem
      boxProps={{ borderBottom: '1px solid', padding: 'large' }}
      disableHover
    >
      <Box
        alignItems="center"
        backgroundColor="whiteGrey"
        border="1px solid"
        borderColor="hoverGrey"
        borderRadius={5}
        height={80}
        justifyContent="center"
        marginRight="large"
        width={240}
      >
        <Image borderRadius={5} height={240} marginRight="medium" backgroundSize="contain" url={preview_image} />
      </Box>

      <Box justifyContent="center" flexDirection="column" minWidth="0">
        <Text fontSize="small">{title}</Text>

        <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small" variant="ellipsis">
          {short_name}
        </Text>

        <Text color="faintGrey" fontSize="xsmall" marginTop="small">
          {`${width} x ${height}mm - ${brand.title}`}
        </Text>
      </Box>

      <Box alignItems="center" flexDirection="row" justifyContent="flex-end" marginRight="0" minWidth="0">
        <IconWithMenu
          icon={(
            <Link greyLink>
              <FontAwesomeIcon icon={faCog} />
            </Link>
          )}
        >
          <IconWithMenu.Menu closeOnClick listStyle={{ right: '-22px' }}>
            {shouldRenderEditPartial && (
              <Link key="edit" onClick={() => loginToRealhub()}>
                <FontAwesomeIcon icon={faPencil} /> Edit Partial
              </Link>
            )}

            <Link
              key="duplicate"
              onClick={() => confirmDuplicateTemplatePagePartial(
                { duplicateFn, duplicateTemplatePagePartial },
              )}
            >
              <FontAwesomeIcon icon={faCopy} /> Duplicate Partial
            </Link>

            <Link
              key="update"
              onClick={() => confirmToggleVisibility(
                {
                  toggleTemplatePagePartialVisibility,
                  toggleVisibilityFn,
                  templatePagePartial,
                },
              )}
            >
              <FontAwesomeIcon icon={hidden ? faRotateLeft : faArchive} />
              {hidden ? 'Unarchive Partial' : 'Archive Partial'}
            </Link>
          </IconWithMenu.Menu>
        </IconWithMenu>
      </Box>
    </ListItem>
  )
}

export default TemplatePagePartial
